import React from "react";
import { katalogVergessen } from "../utils/katalogCache";
import { useLocation, useNavigate } from "react-router";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Subtitle1,
  Spinner,
  Text,
  Toast,
  ToastBody,
  ToastTitle,
  Toaster,
  Tooltip,
  Title1,
  useId,
  useToastController,
} from "@fluentui/react-components";
import { ArrowMoveRegular, Info16Regular } from "@fluentui/react-icons";
import {
  type DashboardCardSpan,
  type DashboardCardId,
  dashboardCardDefinitions,
  useAppSettings,
} from "../context/AppSettingsContext";
import { apiUrl } from "../utils/api";
import { normalizeLegalFormOptions } from "../utils/legalFormOptions";
import { useAuth } from "../context/AuthContext";
import NeuwertIndexPflege from "../components/settings/NeuwertIndexPflege";
import { UserManagementSection } from "./settings/UserManagementSection";
import { ListsSection } from "./settings/ListsSection";
import { HaushaltListsSection } from "./settings/HaushaltListsSection";
import { MailboxAccessSection } from "./settings/MailboxAccessSection";
import { SpartenSection } from "./settings/SpartenSection";
import { BranchenKatalogSection } from "./settings/BranchenKatalogSection";
import { KontaktRollenSection } from "./settings/KontaktRollenSection";
import { RiskObjectMappingSection } from "./settings/RiskObjectMappingSection";
import { SepaCreditorSection } from "./settings/SepaCreditorSection";
import { RemarkableSection } from "./settings/RemarkableSection";
import SecuritySettings from "./settings/SecuritySettings";
import { NewsSection } from "./settings/NewsSection";
import { PdfBausteineSection } from "./settings/PdfBausteineSection";
import { OfficeOperationsSection } from "./settings/OfficeOperationsSection";
import { KontaktFreigabenSection } from "./settings/KontaktFreigabenSection";
import { DiktatSection } from "./settings/DiktatSection";
import { alleFenster } from "../components/windowManager/browserFenster/fensterRegister";

type SettingsSection =
  | "office-betrieb"
  | "profile"
  | "dashboard"
  | "application-manager"
  | "lists"
  | "haushalt-listen"
  | "sparten"
  | "branchen-katalog"
  | "kontakt-rollen"
  | "risiko-zuordnung"
  | "sepa-bankdaten"
  | "remarkable"
  | "diktat"
  | "user-management"
  | "mailbox-access"
  | "kontakt-freigaben"
  | "news"
  | "pdf-bausteine"
  | "security";

/**
 * Abschnitte, die per `?bereich=` direkt geöffnet werden dürfen — z. B. aus dem PDF-Editor
 * („Unterschriften verwalten…“ → `/settings?bereich=pdf-bausteine`). Rechte prüft weiter
 * der jeweilige Render-Zweig.
 */
const DIREKT_OEFFENBAR: readonly SettingsSection[] = [
  "office-betrieb",
  "profile",
  "pdf-bausteine",
  "security",
  // Mitteilung „Genehmigung erbeten" verlinkt hierher (30.09.2026).
  "kontakt-freigaben",
];

const abschnittAusSuche = (search: string): SettingsSection | null => {
  const bereich = new URLSearchParams(search).get("bereich");
  return DIREKT_OEFFENBAR.find((a) => a === bereich) ?? null;
};

const abschnittAusUrl = (): SettingsSection => {
  if (typeof window === "undefined") return "profile";
  return abschnittAusSuche(window.location.search) ?? "profile";
};

type MasterDataKey =
  | "contactSalutation"
  | "contactTitle"
  | "contactLegalForm";

type MasterDataItem = {
  id: number;
  label: string;
};

type MasterDataListState = {
  items: MasterDataItem[];
  loading: boolean;
  saving: boolean;
  error: string;
  draftValue: string;
  editingId: number | null;
  editingValue: string;
};

type MasterDataConfig = {
  key: MasterDataKey;
  title: string;
  endpoint: string;
  fieldName: "salutation" | "title" | "legalForm";
};

const masterDataConfigs: MasterDataConfig[] = [
  {
    key: "contactSalutation",
    title: "Anrede",
    endpoint: "contactsalutation",
    fieldName: "salutation",
  },
  {
    key: "contactTitle",
    title: "Titel",
    endpoint: "contact-title",
    fieldName: "title",
  },
  {
    key: "contactLegalForm",
    title: "Gesellschaftsform",
    endpoint: "contact-legal-form",
    fieldName: "legalForm",
  },
];

const futureManagedLists = [
  {
    key: "branche",
    title: "Branche",
  },
  {
    key: "geschlecht",
    title: "Geschlecht",
  },
  {
    key: "berufsstatus",
    title: "Berufsstatus",
  },
];

const createInitialMasterDataState = (): Record<MasterDataKey, MasterDataListState> => ({
  contactSalutation: {
    items: [],
    loading: true,
    saving: false,
    error: "",
    draftValue: "",
    editingId: null,
    editingValue: "",
  },
  contactTitle: {
    items: [],
    loading: true,
    saving: false,
    error: "",
    draftValue: "",
    editingId: null,
    editingValue: "",
  },
  contactLegalForm: {
    items: [],
    loading: true,
    saving: false,
    error: "",
    draftValue: "",
    editingId: null,
    editingValue: "",
  },
});

const normalizeMasterDataItems = (
  config: MasterDataConfig,
  payload: unknown,
): MasterDataItem[] => {
  if (config.fieldName === "legalForm") {
    return normalizeLegalFormOptions(payload).map((item) => ({
      id: item.id,
      label: item.legalForm,
    }));
  }

  const list: any[] = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as any)?.items)
      ? (payload as any).items
      : [];

  return list
    .map((entry: any) => ({
      id: Number(entry?.id),
      label: String(entry?.[config.fieldName] ?? entry?.name ?? ""),
    }))
    .filter((entry) => Number.isFinite(entry.id) && entry.label.trim().length > 0);
};

const sectionButtonStyle = (selected: boolean): React.CSSProperties => ({
  minWidth: 150,
  justifyContent: "center",
  borderColor: selected ? "#361836" : undefined,
});

const SectionTitleWithTooltip: React.FC<{
  title: string;
  description: string;
}> = ({ title, description }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    }}
  >
    <span>{title}</span>
    <Tooltip content={description} relationship="label">
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          color: "#666",
          cursor: "help",
        }}
      >
        <Info16Regular />
      </span>
    </Tooltip>
  </span>
);

const SortableDashboardSettingsCard: React.FC<{
  cardId: DashboardCardId;
  title: string;
  description: string;
  isVisible: boolean;
  selectedSpan: DashboardCardSpan;
  onToggleVisible: (visible: boolean) => void;
  onSetSpan: (span: DashboardCardSpan) => void;
}> = ({
  cardId,
  title,
  description,
  isVisible,
  selectedSpan,
  onToggleVisible,
  onSetSpan,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id: cardId });

  return (
    <Card
      ref={setNodeRef}
      style={{
        padding: 16,
        border: "1px solid #ececec",
        borderColor: isOver ? "#361836" : "#ececec",
        borderStyle: isOver ? "dashed" : "solid",
        borderRadius: 8,
        background: isDragging ? "#f7f4f7" : "#fff",
        boxShadow: isDragging
          ? "0 4px 12px rgba(0,0,0,0.08)"
          : isOver
            ? "0 0 0 2px rgba(54,24,54,0.12)"
            : "none",
        transform: CSS.Transform.toString(transform),
        transition,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        opacity: isVisible ? 1 : 0.7,
        position: "relative",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Checkbox
            checked={isVisible}
            label={title}
            onChange={(_, data) => onToggleVisible(Boolean(data.checked))}
          />
        </div>
        {isVisible ? (
          <Tooltip content="Ziehen, um die Reihenfolge zu ändern" relationship="label">
            <button
              type="button"
              {...attributes}
              {...listeners}
              style={{
                cursor: "grab",
                border: "1px solid #ddd",
                background: "#fafafa",
                borderRadius: 6,
                padding: "6px 8px",
                lineHeight: 1,
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              aria-label={`${title} verschieben`}
            >
              <ArrowMoveRegular />
            </button>
          </Tooltip>
        ) : (
          <span
            style={{
              width: 40,
              height: 32,
              flexShrink: 0,
            }}
          />
        )}
      </div>
      <Text style={{ color: "#666", fontSize: 13 }}>{description}</Text>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <Text style={{ color: "#666", fontSize: 12 }}>Breite</Text>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {dashboardSpanOptions.map((option) => (
            <Tooltip key={option.value} content={option.label} relationship="label">
              <Button
                size="small"
                appearance={selectedSpan === option.value ? "primary" : "secondary"}
                style={{ minWidth: 44 }}
                onClick={() => onSetSpan(option.value)}
              >
                <SpanPreviewIcon
                  span={option.value}
                  selected={selectedSpan === option.value}
                />
              </Button>
            </Tooltip>
          ))}
        </div>
      </div>
    </Card>
  );
};

const dashboardSpanOptions: Array<{
  value: DashboardCardSpan;
  label: string;
}> = [
  { value: 1, label: "1 Spalte" },
  { value: 2, label: "2 Spalten" },
  { value: 3, label: "3 Spalten" },
  { value: 4, label: "4 Spalten" },
];

const SpanPreviewIcon: React.FC<{
  span: DashboardCardSpan;
  selected: boolean;
}> = ({ span, selected }) => {
  const activeColor = selected ? "#ffffff" : "#0f6cbd";
  const inactiveColor = selected ? "rgba(255,255,255,0.38)" : "#c8c6c4";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
      }}
    >
      {[1, 2, 3, 4].map((column) => (
        <span
          key={column}
          style={{
            width: 6,
            height: 12,
            borderRadius: 2,
            backgroundColor: column <= span ? activeColor : inactiveColor,
            display: "inline-block",
          }}
        />
      ))}
    </span>
  );
};

const SettingsPage: React.FC = () => {
  const { isAdmin, isGfgl, user } = useAuth();
  const canManageUsers = isAdmin || isGfgl;
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = React.useState<SettingsSection>(abschnittAusUrl);
  // Ein Mitteilungs-Link auf `/settings?bereich=…` bei bereits offener Seite
  // soll den Abschnitt ebenfalls umschalten, nicht nur beim ersten Aufbau.
  const { search } = useLocation();
  React.useEffect(() => {
    const aus = abschnittAusSuche(search);
    if (aus) setActiveSection(aus);
  }, [search]);
  const [profileInfo, setProfileInfo] = React.useState({
    name: "",
    username: "",
    tenantId: "",
  });
  const [viewportWidth, setViewportWidth] = React.useState(() =>
    typeof window === "undefined" ? 1280 : window.innerWidth,
  );
  const [masterData, setMasterData] = React.useState<Record<MasterDataKey, MasterDataListState>>(
    () => createInitialMasterDataState(),
  );
  const [activeListManagerKey, setActiveListManagerKey] = React.useState<MasterDataKey | null>(null);
  const [activeDraggedCardId, setActiveDraggedCardId] = React.useState<DashboardCardId | null>(null);
  const toasterId = useId("settings-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const {
    visibleDashboardCardIds,
    dashboardCardSpans,
    setDashboardCardVisibility,
    setDashboardCardSpan,
    moveDashboardCard,
    resetDashboardCards,
  } =
    useAppSettings();
  const isMobile = viewportWidth < 768;
  const isTablet = viewportWidth < 1100;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );

  const updateMasterDataState = React.useCallback(
    (
      key: MasterDataKey,
      updater: (current: MasterDataListState) => MasterDataListState,
    ) => {
      setMasterData((current) => ({
        ...current,
        [key]: updater(current[key]),
      }));
    },
    [],
  );

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    alleFenster.addEventListener("resize", handleResize);
    return () => {
      alleFenster.removeEventListener("resize", handleResize);
    };
  }, []);

  React.useEffect(() => {
    let mounted = true;

    const loadProfile = () => {
      if (!mounted) return;
      const u: any = user ?? {};
      const computed = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
      setProfileInfo({
        name: u.name ?? u.displayName ?? computed ?? "",
        username: u.email ?? u.username ?? u.name ?? "",
        tenantId: "",
      });
    };

    loadProfile();

    return () => {
      mounted = false;
    };
  }, [user]);

  React.useEffect(() => {
    let mounted = true;

    const loadMasterData = async () => {
      await Promise.all(
        masterDataConfigs.map(async (config) => {
          updateMasterDataState(config.key, (current) => ({
            ...current,
            loading: true,
            error: "",
          }));

          try {
            const response = await fetch(apiUrl(config.endpoint));
            if (!response.ok) {
              throw new Error(`${response.status} ${response.statusText}`);
            }

            const payload = await response.json();
            if (!mounted) return;

            updateMasterDataState(config.key, (current) => ({
              ...current,
              items: normalizeMasterDataItems(config, payload),
              loading: false,
            }));
          } catch (error) {
            if (!mounted) return;
            const message =
              error instanceof Error
                ? `Laden fehlgeschlagen: ${error.message}`
                : "Laden fehlgeschlagen.";
            updateMasterDataState(config.key, (current) => ({
              ...current,
              items: [],
              loading: false,
              error: "",
            }));
            dispatchToast(
              <Toast>
                <ToastTitle>Fehler beim Laden</ToastTitle>
                <ToastBody>{`${config.title}: ${message}`}</ToastBody>
              </Toast>,
              { intent: "error" },
            );
          }
        }),
      );
    };

    loadMasterData();

    return () => {
      mounted = false;
    };
  }, [dispatchToast, updateMasterDataState]);

  const handleCreateMasterDataItem = async (config: MasterDataConfig) => {
    const currentState = masterData[config.key];
    const value = currentState.draftValue.trim();
    if (!value) return;

    updateMasterDataState(config.key, (current) => ({
      ...current,
      saving: true,
      error: "",
    }));

    try {
      const response = await fetch(apiUrl(config.endpoint), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [config.fieldName]: value }),
      });

      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      // Die Masken halten die Kataloge gecacht (utils/katalogCache.ts).
      katalogVergessen(config.endpoint);

      const payload = await response.json();
      const createdItems = normalizeMasterDataItems(config, payload);
      const createdItem =
        createdItems[0] ??
        {
          id: Number((payload as any)?.id ?? Date.now()),
          label: String((payload as any)?.[config.fieldName] ?? value),
        };

      updateMasterDataState(config.key, (current) => ({
        ...current,
        items: [...current.items, createdItem].sort((left, right) =>
          left.label.localeCompare(right.label, "de"),
        ),
        saving: false,
        draftValue: "",
        error: "",
      }));
      dispatchToast(
        <Toast>
          <ToastTitle>Eintrag angelegt</ToastTitle>
          <ToastBody>{config.title} wurde erfolgreich hinzugefügt.</ToastBody>
        </Toast>,
        { intent: "success" },
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? `Anlegen fehlgeschlagen: ${error.message}`
          : "Anlegen fehlgeschlagen.";
      updateMasterDataState(config.key, (current) => ({
        ...current,
        saving: false,
        error: "",
      }));
      dispatchToast(
        <Toast>
          <ToastTitle>Fehler beim Anlegen</ToastTitle>
          <ToastBody>{`${config.title}: ${message}`}</ToastBody>
        </Toast>,
        { intent: "error" },
      );
    }
  };

  const startEditingMasterDataItem = (config: MasterDataConfig, item: MasterDataItem) => {
    updateMasterDataState(config.key, (current) => ({
      ...current,
      editingId: item.id,
      editingValue: item.label,
      error: "",
    }));
  };

  const cancelEditingMasterDataItem = (config: MasterDataConfig) => {
    updateMasterDataState(config.key, (current) => ({
      ...current,
      editingId: null,
      editingValue: "",
    }));
  };

  const handleUpdateMasterDataItem = async (config: MasterDataConfig) => {
    const currentState = masterData[config.key];
    if (currentState.editingId == null) return;

    const value = currentState.editingValue.trim();
    if (!value) return;

    updateMasterDataState(config.key, (current) => ({
      ...current,
      saving: true,
      error: "",
    }));

    try {
      const response = await fetch(apiUrl(`${config.endpoint}/${currentState.editingId}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [config.fieldName]: value }),
      });

      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      // Die Masken halten die Kataloge gecacht (utils/katalogCache.ts).
      katalogVergessen(config.endpoint);

      updateMasterDataState(config.key, (current) => ({
        ...current,
        items: current.items
          .map((item) =>
            item.id === current.editingId ? { ...item, label: value } : item,
          )
          .sort((left, right) => left.label.localeCompare(right.label, "de")),
        saving: false,
        editingId: null,
        editingValue: "",
        error: "",
      }));
      dispatchToast(
        <Toast>
          <ToastTitle>Eintrag gespeichert</ToastTitle>
          <ToastBody>{config.title} wurde erfolgreich aktualisiert.</ToastBody>
        </Toast>,
        { intent: "success" },
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? `Speichern fehlgeschlagen: ${error.message}`
          : "Speichern fehlgeschlagen.";
      updateMasterDataState(config.key, (current) => ({
        ...current,
        saving: false,
        error: "",
      }));
      dispatchToast(
        <Toast>
          <ToastTitle>Fehler beim Speichern</ToastTitle>
          <ToastBody>{`${config.title}: ${message}`}</ToastBody>
        </Toast>,
        { intent: "error" },
      );
    }
  };

  const handleDeleteMasterDataItem = async (
    config: MasterDataConfig,
    item: MasterDataItem,
  ) => {
    updateMasterDataState(config.key, (current) => ({
      ...current,
      saving: true,
      error: "",
    }));

    try {
      const response = await fetch(apiUrl(`${config.endpoint}/${item.id}`), {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }
      // Die Masken halten die Kataloge gecacht (utils/katalogCache.ts).
      katalogVergessen(config.endpoint);

      updateMasterDataState(config.key, (current) => ({
        ...current,
        items: current.items.filter((entry) => entry.id !== item.id),
        saving: false,
        editingId: current.editingId === item.id ? null : current.editingId,
        editingValue: current.editingId === item.id ? "" : current.editingValue,
        error: "",
      }));
      dispatchToast(
        <Toast>
          <ToastTitle>Eintrag gelöscht</ToastTitle>
          <ToastBody>{config.title} wurde erfolgreich gelöscht.</ToastBody>
        </Toast>,
        { intent: "success" },
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? `Löschen fehlgeschlagen: ${error.message}`
          : "Löschen fehlgeschlagen.";
      updateMasterDataState(config.key, (current) => ({
        ...current,
        saving: false,
        error: "",
      }));
      dispatchToast(
        <Toast>
          <ToastTitle>Fehler beim Löschen</ToastTitle>
          <ToastBody>{`${config.title}: ${message}`}</ToastBody>
        </Toast>,
        { intent: "error" },
      );
    }
  };

  const handleDashboardCardDragStart = (event: DragStartEvent) => {
    setActiveDraggedCardId(event.active.id as DashboardCardId);
  };

  const handleDashboardCardDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDraggedCardId(null);
    if (!over || active.id === over.id) return;

    moveDashboardCard(active.id as DashboardCardId, over.id as DashboardCardId);
  };

  const activeDraggedCard =
    activeDraggedCardId != null
      ? dashboardCardDefinitions.find((card) => card.id === activeDraggedCardId) ?? null
      : null;

  const enabledDashboardCards = visibleDashboardCardIds
    .map((cardId) => dashboardCardDefinitions.find((card) => card.id === cardId))
    .filter((card): card is (typeof dashboardCardDefinitions)[number] => Boolean(card));
  const hiddenDashboardCards = dashboardCardDefinitions.filter(
    (card) => !visibleDashboardCardIds.includes(card.id),
  );
  const activeListConfig =
    masterDataConfigs.find((config) => config.key === activeListManagerKey) ?? null;
  const activeListState = activeListConfig ? masterData[activeListConfig.key] : null;
  const listManagementColumns = isMobile
    ? "minmax(0, 1fr)"
    : isTablet
      ? "repeat(2, minmax(0, 1fr))"
      : "repeat(3, minmax(0, 1fr))";

  return (
    <div
      style={{
        padding: isMobile ? 16 : 32,
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? 16 : 24,
      }}
    >
      <Toaster toasterId={toasterId} position="top-end" />
      <div>
        <Title1>Einstellungen</Title1>
      </div>

      {activeSection === 'office-betrieb' && isAdmin && <Card><OfficeOperationsSection/></Card>}
      <Card style={{ padding: isMobile ? 16 : 20 }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: isMobile ? "nowrap" : "wrap",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Button
            appearance={activeSection === "profile" ? "primary" : "secondary"}
            style={{
              ...sectionButtonStyle(activeSection === "profile"),
              width: isMobile ? "100%" : undefined,
            }}
            onClick={() => setActiveSection("profile")}
          >
            Profil
          </Button>
          <Button
            appearance={activeSection === "application-manager" ? "primary" : "secondary"}
            style={{
              ...sectionButtonStyle(activeSection === "application-manager"),
              width: isMobile ? "100%" : undefined,
            }}
            onClick={() => setActiveSection("application-manager")}
          >
            Anwendungsmanager
          </Button>
          {isAdmin && <Button appearance={activeSection === 'office-betrieb' ? 'primary' : 'secondary'} onClick={()=>setActiveSection('office-betrieb')}>Office-Betrieb</Button>}
          {canManageUsers && (
            <Button
              appearance={activeSection === "lists" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "lists"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("lists")}
            >
              Listen
            </Button>
          )}
          {canManageUsers && (
            <Button
              appearance={activeSection === "haushalt-listen" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "haushalt-listen"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("haushalt-listen")}
            >
              {"Haushalt & Beziehungen"}
            </Button>
          )}
          {canManageUsers && (
            <Button
              appearance={activeSection === "sparten" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "sparten"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("sparten")}
            >
              Sparten
            </Button>
          )}
          {isAdmin && (
            <Button
              appearance={activeSection === "sepa-bankdaten" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "sepa-bankdaten"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("sepa-bankdaten")}
            >
              SEPA-Bankdaten
            </Button>
          )}
          {isAdmin && (
            <Button
              appearance={activeSection === "remarkable" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "remarkable"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("remarkable")}
            >
              reMarkable
            </Button>
          )}
          {isAdmin && (
            <Button
              appearance={activeSection === "diktat" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "diktat"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("diktat")}
            >
              Diktieren
            </Button>
          )}
          {canManageUsers && (
            <Button
              appearance={activeSection === "branchen-katalog" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "branchen-katalog"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("branchen-katalog")}
            >
              Branchen-Katalog
            </Button>
          )}
          {isAdmin && (
            <Button
              appearance={activeSection === "kontakt-rollen" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "kontakt-rollen"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("kontakt-rollen")}
            >
              Kontaktrollen
            </Button>
          )}
          {canManageUsers && (
            <Button
              appearance={activeSection === "risiko-zuordnung" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "risiko-zuordnung"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("risiko-zuordnung")}
            >
              Risiko-Zuordnung
            </Button>
          )}
          {canManageUsers && (
            <Button
              appearance={activeSection === "user-management" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "user-management"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("user-management")}
            >
              Benutzerverwaltung
            </Button>
          )}
          {canManageUsers && (
            <Button
              appearance={activeSection === "mailbox-access" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "mailbox-access"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("mailbox-access")}
            >
              Postfach-Freigaben
            </Button>
          )}
          {/* Seit 01.10.2026 für alle: FO/BO entscheiden die Anfragen zu ihren
              Kontakten (die Liste filtert der Server). */}
          {(
            <Button
              appearance={activeSection === "kontakt-freigaben" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "kontakt-freigaben"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("kontakt-freigaben")}
            >
              Kontakt-Freigaben
            </Button>
          )}
          {canManageUsers && (
            <Button
              appearance={activeSection === "news" ? "primary" : "secondary"}
              style={{
                ...sectionButtonStyle(activeSection === "news"),
                width: isMobile ? "100%" : undefined,
              }}
              onClick={() => setActiveSection("news")}
            >
              {"Courté Intern / News"}
            </Button>
          )}
          <Button
            appearance="secondary"
            style={{ ...sectionButtonStyle(false), width: isMobile ? "100%" : undefined }}
            onClick={() => navigate("/einstellungen/tickets")}
          >
            {"Tickets"}
          </Button>
          <Button
            appearance={activeSection === "pdf-bausteine" ? "primary" : "secondary"}
            style={{
              ...sectionButtonStyle(activeSection === "pdf-bausteine"),
              width: isMobile ? "100%" : undefined,
            }}
            onClick={() => setActiveSection("pdf-bausteine")}
          >
            {"Unterschriften & Stempel"}
          </Button>
          <Button
            appearance={activeSection === "security" ? "primary" : "secondary"}
            style={{
              ...sectionButtonStyle(activeSection === "security"),
              width: isMobile ? "100%" : undefined,
            }}
            onClick={() => setActiveSection("security")}
          >
            Sicherheit
          </Button>
        </div>
      </Card>

      {activeSection === "lists" && canManageUsers ? <ListsSection /> : null}

      {activeSection === "haushalt-listen" && canManageUsers ? <HaushaltListsSection /> : null}

      {activeSection === "sparten" && canManageUsers ? <SpartenSection /> : null}
      {/* Nur Admin: Die Endpunkte dahinter sind `@Roles('admin')` — ein Knopf,
          der zu einem 403 führt, ist schlimmer als keiner. */}
      {activeSection === "sepa-bankdaten" && isAdmin ? <SepaCreditorSection /> : null}
      {/* Nur Admin: Die Endpunkte dahinter sind `@Roles('admin')`. */}
      {activeSection === "remarkable" && isAdmin ? <RemarkableSection /> : null}
      {/* Nur Admin: `PUT /api/diktat/einstellung` ist `@Roles('admin')`. */}
      {activeSection === "diktat" && isAdmin ? <DiktatSection /> : null}

      {activeSection === "branchen-katalog" && canManageUsers ? <BranchenKatalogSection /> : null}

      {activeSection === "kontakt-rollen" && isAdmin ? <KontaktRollenSection /> : null}

      {activeSection === "pdf-bausteine" ? <PdfBausteineSection /> : null}

      {activeSection === "risiko-zuordnung" && canManageUsers ? <RiskObjectMappingSection /> : null}

      {activeSection === "user-management" && canManageUsers ? (
        <UserManagementSection />
      ) : null}

      {activeSection === "mailbox-access" && canManageUsers ? (
        <MailboxAccessSection />
      ) : null}

      {activeSection === "news" && canManageUsers ? <NewsSection /> : null}

      {activeSection === "kontakt-freigaben" ? <KontaktFreigabenSection /> : null}

      {activeSection === "security" ? (
        <SecuritySettings />
      ) : null}

      {activeSection === "dashboard" ? (
        <Card
          style={{
            padding: isMobile ? 16 : 24,
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? 16 : 20,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div>
              <Subtitle1>
                <SectionTitleWithTooltip
                  title="Dashboard-Karten"
                  description="Diese Auswahl wird pro Benutzer gespeichert und bei der nächsten Anmeldung wiederhergestellt."
                />
              </Subtitle1>
            </div>
            <Button appearance="secondary" onClick={resetDashboardCards}>
              Standard wiederherstellen
            </Button>
          </div>

          <Text style={{ color: "#666" }}>
            Aktive Karten lassen sich direkt ueber das kleine Griffsymbol auf der Karte
            umsortieren und werden pro Benutzer gespeichert.
          </Text>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDashboardCardDragStart}
            onDragEnd={handleDashboardCardDragEnd}
            onDragCancel={() => setActiveDraggedCardId(null)}
          >
            <SortableContext
              items={enabledDashboardCards.map((card) => card.id)}
              strategy={rectSortingStrategy}
            >
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
                {enabledDashboardCards.map((card) => (
                  <SortableDashboardSettingsCard
                    key={card.id}
                    cardId={card.id}
                    title={card.title}
                    description={card.description}
                    isVisible={visibleDashboardCardIds.includes(card.id)}
                    selectedSpan={dashboardCardSpans[card.id]}
                    onToggleVisible={(visible) => setDashboardCardVisibility(card.id, visible)}
                    onSetSpan={(span) => setDashboardCardSpan(card.id, span)}
                  />
                ))}
                {hiddenDashboardCards.map((card) => (
                  <Card
                    key={card.id}
                    style={{
                      padding: 16,
                      border: "1px solid #ececec",
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      opacity: 0.7,
                    }}
                  >
                    <Checkbox
                      checked={false}
                      label={card.title}
                      onChange={(_, data) =>
                        setDashboardCardVisibility(card.id, Boolean(data.checked))
                      }
                    />
                    <Text style={{ color: "#666", fontSize: 13 }}>{card.description}</Text>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <Text style={{ color: "#666", fontSize: 12 }}>Breite</Text>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {dashboardSpanOptions.map((option) => (
                          <Tooltip key={option.value} content={option.label} relationship="label">
                            <Button
                              size="small"
                              appearance={
                                dashboardCardSpans[card.id] === option.value ? "primary" : "secondary"
                              }
                              style={{ minWidth: 44 }}
                              onClick={() => setDashboardCardSpan(card.id, option.value)}
                            >
                              <SpanPreviewIcon
                                span={option.value}
                                selected={dashboardCardSpans[card.id] === option.value}
                              />
                            </Button>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeDraggedCard ? (
                <Card
                  style={{
                    padding: 16,
                    border: "1px solid #d6d6d6",
                    borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
                    width: isMobile ? 240 : 300,
                    background: "#fff",
                    opacity: 0.95,
                  }}
                >
                  <Text style={{ fontWeight: 600 }}>{activeDraggedCard.title}</Text>
                  <Text style={{ color: "#666", fontSize: 13 }}>
                    Wird verschoben…
                  </Text>
                </Card>
              ) : null}
            </DragOverlay>
          </DndContext>
        </Card>
      ) : null}

      {activeSection === "profile" ? (
        <Card
          style={{
            padding: isMobile ? 16 : 24,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <Subtitle1>
            <SectionTitleWithTooltip
              title="Benutzerprofil"
              description="Hier passen typischerweise Sprache, Standardansichten, Benachrichtigungen, Signaturen, Datenschutz- und Sicherheitsoptionen hinein."
            />
          </Subtitle1>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
            }}
          >
            <Card style={{ padding: 16, border: "1px solid #ececec" }}>
              <Text style={{ fontWeight: 600 }}>Name</Text>
              <div style={{ marginTop: 8 }}>{profileInfo.name || "-"}</div>
            </Card>
            <Card style={{ padding: 16, border: "1px solid #ececec" }}>
              <Text style={{ fontWeight: 600 }}>Benutzer</Text>
              <div style={{ marginTop: 8 }}>{profileInfo.username || "-"}</div>
            </Card>
            <Card style={{ padding: 16, border: "1px solid #ececec" }}>
              <Text style={{ fontWeight: 600 }}>Tenant</Text>
              <div style={{ marginTop: 8 }}>{profileInfo.tenantId || "-"}</div>
            </Card>
          </div>
        </Card>
      ) : null}

      {activeSection === "application-manager" ? (
        <Card
          style={{
            padding: isMobile ? 16 : 24,
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? 16 : 20,
          }}
        >

          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Subtitle1>
                <SectionTitleWithTooltip
                  title="Listenverwaltung"
                  description="Pflege zentrale Stammdatenlisten direkt in der Anwendung. Die ersten drei Listen sind bereits an vorhandene Endpunkte angebunden."
                />
              </Subtitle1>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: listManagementColumns,
                  gap: 16,
                }}
              >
                {masterDataConfigs.map((config) => {
                  const state = masterData[config.key];
                  return (
                    <Card
                      key={config.key}
                      style={{
                        padding: 16,
                        border: "1px solid #ececec",
                        display: "flex",
                        flexDirection: "column",
                        gap: 12,
                      }}
                    >
                      <Text style={{ fontWeight: 600 }}>{config.title}</Text>

                      {state.loading ? (
                        <div style={{ display: "flex", justifyContent: "center", padding: "12px 0" }}>
                          <Spinner size="tiny" />
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {state.items.length === 0 ? (
                            <Text style={{ color: "#666", fontSize: 13 }}>Keine Einträge vorhanden.</Text>
                          ) : (
                            state.items.slice(0, 3).map((item) => (
                              <div
                                key={item.id}
                                style={{
                                  padding: "8px 0",
                                  borderBottom: "1px solid #f1f1f1",
                                }}
                              >
                                {item.label}
                              </div>
                            ))
                          )}
                          {state.items.length > 3 ? (
                            <Text style={{ color: "#666", fontSize: 12 }}>
                              + {state.items.length - 3} weitere Einträge
                            </Text>
                          ) : null}
                        </div>
                      )}

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                          marginTop: "auto",
                        }}
                      >
                        <Text style={{ color: "#666", fontSize: 12 }}>
                          {state.loading
                            ? "Wird geladen..."
                            : `${state.items.length} Einträge`}
                        </Text>
                        <Button
                          appearance="secondary"
                          onClick={() => setActiveListManagerKey(config.key)}
                        >
                          Verwalten
                        </Button>
                      </div>
                    </Card>
                  );
                })}

                {futureManagedLists.map((list) => (
                  <Card
                    key={list.key}
                    style={{
                      padding: 16,
                      border: "1px solid #ececec",
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    <Text style={{ fontWeight: 600 }}>{list.title}</Text>
                    <Text style={{ color: "#666", fontSize: 13 }}>
                      Für diese Liste ist die CRUD-Maske vorbereitet, aber noch nicht an ein
                      Backend gekoppelt.
                    </Text>
                  </Card>
                ))}
              {/* Keine Label-Liste, sondern eine Matrix aus Sparte × Jahr —
                    deshalb eine eigene Maske statt einer weiteren Karte im
                    MasterData-Muster. */}
                <NeuwertIndexPflege />
              </div>
            </div>

            <Card
              style={{
                padding: 16,
                border: "1px solid #ececec",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <Subtitle1>
                <SectionTitleWithTooltip
                  title="Weitere Verwaltungsbereiche"
                  description="Hier koennen spaeter weitere administrative Bereiche wie Regeln, Freigaben oder Systemvorlagen eingebunden werden."
                />
              </Subtitle1>
              <Text style={{ color: "#666", fontSize: 13 }}>
                Dieser Bereich ist als Platzhalter vorbereitet, damit weitere
                Verwaltungsfunktionen unter dem Anwendungsmanager ergänzt werden können.
              </Text>
            </Card>
          </div>
        </Card>
      ) : null}

      <Dialog
        open={Boolean(activeListConfig)}
        onOpenChange={(_, data) => {
          if (!data.open) {
            setActiveListManagerKey(null);
          }
        }}
      >
        <DialogSurface
          style={{
            maxWidth: isMobile ? "calc(100vw - 24px)" : 920,
            width: "100%",
          }}
        >
          <DialogBody>
            <DialogTitle>
              {activeListConfig ? `${activeListConfig.title} verwalten` : "Liste verwalten"}
            </DialogTitle>
            <DialogContent>
              {activeListConfig && activeListState ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Input
                      value={activeListState.draftValue}
                      placeholder={`${activeListConfig.title} hinzufügen`}
                      onChange={(_, data) =>
                        updateMasterDataState(activeListConfig.key, (current) => ({
                          ...current,
                          draftValue: data.value,
                        }))
                      }
                    />
                    <Button
                      appearance="primary"
                      disabled={
                        activeListState.saving ||
                        activeListState.draftValue.trim().length === 0
                      }
                      onClick={() => handleCreateMasterDataItem(activeListConfig)}
                    >
                      Hinzufügen
                    </Button>
                    <Button
                      appearance="secondary"
                      onClick={() => setActiveListManagerKey(null)}
                    >
                      Schließen
                    </Button>
                  </div>

                  {activeListState.loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
                      <Spinner size="small" />
                    </div>
                  ) : activeListState.items.length === 0 ? (
                    <Text style={{ color: "#666" }}>Keine Einträge vorhanden.</Text>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        maxHeight: "60vh",
                        overflowY: "auto",
                        paddingRight: 4,
                      }}
                    >
                      {activeListState.items.map((item) => {
                        const isEditing = activeListState.editingId === item.id;

                        return (
                          <div
                            key={item.id}
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                              padding: "10px 0",
                              borderBottom: "1px solid #f1f1f1",
                            }}
                          >
                            {isEditing ? (
                              <Input
                                value={activeListState.editingValue}
                                onChange={(_, data) =>
                                  updateMasterDataState(activeListConfig.key, (current) => ({
                                    ...current,
                                    editingValue: data.value,
                                  }))
                                }
                              />
                            ) : (
                              <div style={{ flex: 1, minWidth: 0 }}>{item.label}</div>
                            )}

                            {isEditing ? (
                              <>
                                <Button
                                  appearance="primary"
                                  size="small"
                                  disabled={
                                    activeListState.saving ||
                                    activeListState.editingValue.trim().length === 0
                                  }
                                  onClick={() => handleUpdateMasterDataItem(activeListConfig)}
                                >
                                  Speichern
                                </Button>
                                <Button
                                  appearance="secondary"
                                  size="small"
                                  disabled={activeListState.saving}
                                  onClick={() => cancelEditingMasterDataItem(activeListConfig)}
                                >
                                  Abbrechen
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  appearance="secondary"
                                  size="small"
                                  disabled={activeListState.saving}
                                  onClick={() => startEditingMasterDataItem(activeListConfig, item)}
                                >
                                  Bearbeiten
                                </Button>
                                <Button
                                  appearance="subtle"
                                  size="small"
                                  disabled={activeListState.saving}
                                  onClick={() => handleDeleteMasterDataItem(activeListConfig, item)}
                                >
                                  Löschen
                                </Button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : null}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
